import { Download, Share, X } from "lucide-react";
import { useEffect, useState } from "react";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches
    || ("standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true);
}

function isIosSafari() {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(navigator.userAgent) && !/(CriOS|FxiOS|EdgiOS)/.test(navigator.userAgent);
}

export function PwaInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("japa-reader-install-dismissed") === "1");
  const [installed, setInstalled] = useState(isStandalone);
  const ios = isIosSafari();

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  if (installed || dismissed || (!installPrompt && !ios)) return null;

  const dismiss = () => {
    sessionStorage.setItem("japa-reader-install-dismissed", "1");
    setDismissed(true);
  };

  const install = async () => {
    if (ios) {
      setShowIosHelp(true);
      return;
    }
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstallPrompt(null);
  };

  return (
    <aside className="pwa-install-prompt" aria-label="Instalar Japa Reader">
      <button className="pwa-install-close" type="button" onClick={dismiss} aria-label="Fechar sugestão de instalação">
        <X size={16} />
      </button>
      <img src="/icons/icon-192.png" alt="" />
      <div>
        <strong>Instale o Japa Reader</strong>
        {showIosHelp ? (
          <p>Toque em <Share size={14} aria-hidden="true" /> <b>Compartilhar</b> e depois em <b>Adicionar à Tela de Início</b>.</p>
        ) : (
          <p>Acesse sua leitura em tela cheia, como um aplicativo.</p>
        )}
      </div>
      {!showIosHelp && (
        <button className="pwa-install-action" type="button" onClick={() => void install()}>
          <Download size={17} /> {ios ? "Como instalar" : "Instalar"}
        </button>
      )}
    </aside>
  );
}
